import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { performStatusTransition } from '../actions/performStatusTransition';
import { formatMoney } from '../cashbacks/formatMoney';
import type { DisputeRow } from './chargebacksModel';

/**
 * Resolve a chargeback LOST (D-chargebacks): the value-MOVING outcome. It is
 * super_admin-only (capability chargebacks:resolve_loss), the same tier as
 * cashbacks:resolve, withheld from the fraud reviewer (a fraud reviewer resolves WON
 * but hands LOST to a second higher hand, the ban-reversal asymmetry).
 *
 * The clawback is a NEGATIVE LEDGER ENTRY, never a status flip and never a direct
 * balance write: it CREATES a balanceLedger row (kind chargeback_reversal, a negative
 * signed amount deriveBalanceSigned adds) and flips the dispute to `lost`. The
 * original transaction STAYS confirmed. The amount supports a PARTIAL reversal (up to
 * the cashback earned). Both writes are audited for free by the wrapper. The ledger is
 * the shared one manual balance adjustment also writes (D-balance-adjustment).
 */
export const ResolveLostAction = ({
  row,
  now,
}: {
  row: DisputeRow;
  now: number;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { role } = useAccessContext();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState(String(row.cashbackAmount));
  const [note, setNote] = useState('');

  const amountNum = Number(amount);
  const amountValid =
    Number.isFinite(amountNum) &&
    amountNum > 0 &&
    amountNum <= row.cashbackAmount;
  const canSubmit = amountValid && note.trim().length > 0 && !loading;

  const onConfirm = async () => {
    setLoading(true);
    try {
      if (
        !hasAccess(
          { role, accessibleRegions: [] },
          { capability: 'chargebacks:resolve_loss' },
        )
      ) {
        throw new Error('Not permitted');
      }
      const nowIso = new Date(now).toISOString();
      // 1. The clawback: a NEGATIVE signed ledger entry the balance derives from. NOT
      //    a direct balance write and NOT a status flip on the transaction.
      await dataProvider.create('balanceLedger', {
        data: {
          userId: row.userId,
          transactionId: row.id,
          amount: -(Math.round(amountNum * 100) / 100),
          market: row.market,
          kind: 'chargeback_reversal',
          reasonCode: row.disputeReasonCode ?? 'other',
          reason: note.trim(),
          createdAt: nowIso,
        },
      });
      // 2. Flip the dispute to lost through the chokepoint (guarded + audited). The
      //    settlement status is untouched: the transaction stays confirmed.
      await performStatusTransition({
        dataProvider,
        role,
        capability: 'chargebacks:resolve_loss',
        resource: 'transactions',
        id: row.id,
        data: {
          disputeStatus: 'lost',
          resolvedAt: nowIso,
          disputeSource: row.disputeSource,
          disputeReasonCode: row.disputeReasonCode,
          disputeNote: note.trim(),
        },
        previousData: {
          id: row.id,
          disputeStatus: row.disputeStatus,
          resolvedAt: null,
        },
      });
      notify('Dispute resolved lost, cashback clawed back', {
        type: 'success',
      });
      setOpen(false);
      setLoading(false);
      setNote('');
      refresh();
    } catch {
      notify('Action not permitted or failed', { type: 'error' });
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button variant="contained" color="error" onClick={() => setOpen(true)}>
        Resolve lost
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Resolve this dispute lost</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The dispute is upheld against the platform. This claws back the
            cashback as a reversal ledger entry. The order stays confirmed.
            Record the amount and why.
          </DialogContentText>
          <TextField
            label="Clawback amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            error={amount.length > 0 && !amountValid}
            helperText={`Up to ${formatMoney(row.cashbackAmount, row.currency)} (a partial clawback is allowed)`}
            inputProps={{ 'data-testid': 'clawback-amount' }}
          />
          <TextField
            label="Justification"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            helperText="Recorded with the action, for the audit trail"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!canSubmit}
            onClick={onConfirm}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
